<template>
  <v-dialog
    v-model="dialogProps.visibilidade"
    max-width="600px"
    persistent
  >
    <v-card class="pa-5">
      <!-- título -->
      <v-card-title>
        <v-row class="d-flex flex-column text-lg-center">
          <h4 class="headline primary--text text-no-wrap">
            {{ labelBtnAcao }} Curso de Capacitação
          </h4>
          <span class="subtitle-1">Informe os dados do curso</span>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-form
          ref="form"
          v-model="dialogProps.valid"
        >
          <v-row>
            <!-- área de capacitação -->
            <v-col
              cols="12"
              md="12"
            >
              <v-autocomplete
                v-model="cursoCapacitacao.area"
                :items="listaAreasProfissional"
                :rules="[(v) => !!v || 'Selecione uma área']"
                :search-input.sync="areaSearch"
                item-text="nome"
                label="Área de Capacitação"
                placeholder="Digite para pesquisar..."
                persistent-hint
                prepend-icon="emoji_objects"
                required
                return-object
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        A área
                        <strong
                          class="secondary--text"
                        >{{ areaSearch }}
                        </strong>
                        não foi encontrada.
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- nome do curso de capacitação -->
            <v-col cols="12">
              <v-text-field
                v-model.trim="cursoCapacitacao.nome"
                :rules="[(v) => !!v || 'Digite o nome do curso']"
                label="Nome"
                prepend-icon="library_books"
                required
              />
            </v-col>
          </v-row>

          <!-- nome da instituição do curso de capacitação -->
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model.trim="cursoCapacitacao.instituicao"
                :rules="[(v) => !!v || 'Seleciona a instituição']"
                label="Instituição"
                prepend-icon="account_balance"
                required
              />
            </v-col>
          </v-row>

          <!-- período -->
          <v-row>
            <v-col cols="12">
              <h4>Período</h4>
              <v-row>
                <!-- início -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <app-date-select
                    tipo="month"
                    label="Início"
                    tooltip="Data de início do curso de capacitação"
                    :data-inicial="cursoCapacitacao.inicio"
                    :min="minInicioCursoCapacitacao"
                    :max="maxInicioCursoCapacitacao"
                    @return="(value) => (cursoCapacitacao.inicio = value)"
                  />
                </v-col>

                <!-- término -->
                <v-col
                  cols="12"
                  md="6"
                >
                  <app-date-select
                    tipo="month"
                    label="Término"
                    tooltip="Data de término do curso de capacitação"
                    :data-inicial="cursoCapacitacao.termino"
                    :min="minTerminoCursoCapacitacao"
                    :max="maxTerminoCursoCapacitacao"
                    @return="(value) => (cursoCapacitacao.termino = value)"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- carga horária -->
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model.trim="cursoCapacitacao.cargaHoraria"
                :rules="rules.cargaHoraria"
                label="Carga Horária"
                min="1"
                placeholder="horas"
                prepend-icon="access_time"
                required
                type="number"
                @blur="
                  cursoCapacitacao.cargaHoraria = parseInt(
                    cursoCapacitacao.cargaHoraria
                  )
                "
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- ações -->
      <v-card-actions>
        <v-row class="justify-end mr-4">
          <v-btn
            :disabled="!dialogProps.valid"
            class="mr-2"
            color="primary"
            @click="doAcao"
          >
            {{ labelBtnAcao }}
          </v-btn>
          <v-btn
            color="primary"
            outlined
            @click="dialogProps.visibilidade = false"
          >
            Cancelar
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import AppDateSelect from '@/components/core/AppDateSelect';
import Data from '@/modules/data';

const cursoCapacitacaoRepository = RepositoryFactory.get('cursoCapacitacao');

const DEFAULT_CURSO_CAPACITACAO = () => ({
  id: null,
  nome: null,
  cargaHoraria: null,
  instituicao: null,
  inicio: null,
  termino: null,
  area: null,
});

export default {
  name: 'DialogCursoCapacitacao',

  components: { AppDateSelect },

  props: {
    dialogProps: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      ruleNotEmpty: [(v) => (v && !!v.trim()) || 'Campo Obrigatório'],
      cursoCapacitacao: DEFAULT_CURSO_CAPACITACAO(),
      areaSearch: '',
      dataAtual: Data.get('YYYY-MM-DD'),
      rules: {
        cargaHoraria: [(v) => !!v || 'Seleciona a carga horária', (v) => v >= 1 || 'Número inválido'],
      },
    };
  },

  computed: {
    ...mapState({
      perfil: (state) => state.auth.usuario.perfil,
      listaAreasProfissional: (state) => state.perfil.listaAreasProfissional,
    }),
    labelBtnAcao() {
      if (this.dialogProps.acao === 'editar') {
        return 'Editar';
      }
      if (this.dialogProps.acao === 'cadastrar') {
        return 'Adicionar';
      }
      return null;
    },
    minInicioCursoCapacitacao() {
      return '';
    },
    maxInicioCursoCapacitacao() {
      return this.cursoCapacitacao.termino == null ? this.dataAtual : this.cursoCapacitacao.termino;
    },
    minTerminoCursoCapacitacao() {
      return this.cursoCapacitacao.inicio;
    },
    maxTerminoCursoCapacitacao() {
      return this.dataAtual;
    },
  },

  watch: {
    'dialogProps.visibilidade': function (visibilidade) {
      if (visibilidade) {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }

        if (this.dialogProps.acao === 'editar') {
          // deep copy
          this.cursoCapacitacao = JSON.parse(JSON.stringify(this.dialogProps.cursoCapacitacao));
        }
        if (this.dialogProps.acao === 'cadastrar') {
          this.cursoCapacitacao = DEFAULT_CURSO_CAPACITACAO();
        }
      } else {
        this.cursoCapacitacao = DEFAULT_CURSO_CAPACITACAO();
      }
    },
  },

  methods: {
    doAcao() {
      this.cursoCapacitacao.inicio = Data.create(this.cursoCapacitacao.inicio).startOf('month').format('YYYY-MM-DD');
      this.cursoCapacitacao.termino = Data.create(this.cursoCapacitacao.termino).endOf('month').format('YYYY-MM-DD');
      if (this.dialogProps.acao === 'editar') {
        cursoCapacitacaoRepository.update(this.cursoCapacitacao).then((res) => {
          const cursoCapacitacao = res.data;
          const index = this.perfil.cursosCapacitacao.findIndex((c) => c.id === cursoCapacitacao.id);
          this.perfil.cursosCapacitacao.splice(index, 1, cursoCapacitacao);
          this.dialogProps.visibilidade = false;
          this.$store.dispatch('app/success', { response: res, message: 'Curso editado com sucesso!' });
        });
      }

      if (this.dialogProps.acao === 'cadastrar') {
        cursoCapacitacaoRepository.create(this.cursoCapacitacao).then((res) => {
          this.perfil.cursosCapacitacao.push(res.data);
          this.perfil.cursosCapacitacao.sort((a, b) => a.inicio.localeCompare(b.inicio));
          this.dialogProps.visibilidade = false;
          this.$store.dispatch('app/success', { response: res, message: 'Curso cadastrado com sucesso!' });
        });
      }
    },
  },
};
</script>
